import { rem } from 'polished'
import { memo, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import { SVG_ICON_SIZES } from 'LuxKit/SvgIcon'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import getPropertyIcon from 'lib/offer/getPropertyIcon'

const BedGroup = styled.ul`
  display: grid;
  gap: ${rem(8)} ${rem(16)};
  margin: 0;

  // Two columns on mobile, possibly more on tablet and desktop.
  grid-template-columns: repeat(2, 1fr);
  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(auto-fill, minmax(${rem(180)}, 1fr));
  }
`

const BedConfiguration = styled.li`
  display: flex;
  gap: ${rem(8)};
  margin: 0;

  // Align icon with first line of text (in case it wraps).
  align-items: start;
  line-height: ${SVG_ICON_SIZES.S};
`

interface Props {
  /** Non-empty set of available bed groups */
  bedGroups: Utils.NonEmptyArray<App.Property.BedGroup>
}

function PropertyBedGroups({ bedGroups }: Props) {
  const intl = useIntl()

  const bedGroupsTitle = intl.formatMessage({
    id: '-Drykx_fhLTMnDPx3EOX',
    defaultMessage: 'Available beds',
  })

  // For now, we'll arbitrarily display the first bed group.
  const arbitraryBedConfigurations = bedGroups[0].configurations
  const displayBedConfigurations = useMemo(() => arbitraryBedConfigurations.map(({ type, quantity: count, iconName }, key) => {
    const Icon = getPropertyIcon(iconName)
    switch (type) {
      case 'KING_BED':
        return {
          key,
          name: intl.formatMessage({
            id: 'MVPbjo6sOrw0MZOcyF2i',
            defaultMessage: '{count, plural, one {1x King Bed} other {#x King Beds}}',
          }, { count }),
          Icon,
        }
      case 'QUEEN_BED':
        return {
          key,
          name: intl.formatMessage({
            id: 'iSDrc-iTzHooHQ7BbhNW',
            defaultMessage: '{count, plural, one {1x Queen Bed} other {#x Queen Beds}}',
          }, { count }),
          Icon,
        }
      case 'DOUBLE_BED':
        return {
          key,
          name: intl.formatMessage({
            id: 'S6hBQmKaaMmeGhb-DfOj',
            defaultMessage: '{count, plural, one {1x Double Bed} other {#x Double Beds}}',
          }, { count }),
          Icon,
        }
      case 'FULL_BED':
        return {
          key,
          name: intl.formatMessage({
            id: 'axO-0CD4Ei8UJ2JirIU0',
            defaultMessage: '{count, plural, one {1x Full Bed} other {#x Full Beds}}',
          }, { count }),
          Icon,
        }
      case 'SINGLE_BED':
        return {
          key,
          name: intl.formatMessage({
            id: 'Cw4P2DpXGMhnRl5DtpEC',
            defaultMessage: '{count, plural, one {1x Single Bed} other {#x Single Beds}}',
          }, { count }),
          Icon,
        }
      case 'TWIN_BED':
        return {
          key,
          name: intl.formatMessage({
            id: 'yW2b7PHVGL-Il0521Prl',
            defaultMessage: '{count, plural, one {1x Twin Bed} other {#x Twin Beds}}',
          }, { count }),
          Icon,
        }
      case 'TWIN_XL_BED':
        return {
          key,
          name: intl.formatMessage({
            id: 'e4VUog1kOQm0eVxv068R',
            defaultMessage: '{count, plural, one {1x Twin XL Bed} other {#x Twin XL Beds}}',
          }, { count }),
          Icon,
        }
      case 'SOFA_BED':
        return {
          key,
          name: intl.formatMessage({
            id: 'y1O3gHtyMoWarr04GWA8',
            defaultMessage: '{count, plural, one {1x Sofa Bed} other {#x Sofa Beds}}',
          }, { count }),
          Icon,
        }
      case 'DOUBLE_SOFA_BED':
        return {
          key,
          name: intl.formatMessage({
            id: '8TIA0Jy9E8hKwjawh3Jn',
            defaultMessage: '{count, plural, one {1x Double Sofa Bed} other {#x Double Sofa Beds}}',
          }, { count }),
          Icon,
        }
      case 'QUEEN_SOFA_BED':
        return {
          key,
          name: intl.formatMessage({
            id: 'VDF5AM529ZZwsrvGYIgj',
            defaultMessage: '{count, plural, one {1x Queen Sofa Bed} other {#x Queen Sofa Beds}}',
          }, { count }),
          Icon,
        }
      case 'BUNK_BED':
        return {
          key,
          name: intl.formatMessage({
            id: 'hgSjKxbGgsHo6c9RA4Xw',
            defaultMessage: '{count, plural, one {1x Bunk Bed} other {#x Bunk Beds}}',
          }, { count }),
          Icon,
        }
      case 'ROLL_AWAY_BED':
        return {
          key,
          name: intl.formatMessage({
            id: '8mE0uDVgsW5CtyokptHk',
            defaultMessage: '{count, plural, one {1x Roll Away Bed} other {#x Roll Away Beds}}',
          }, { count }),
          Icon,
        }
      case 'DAY_BED':
        return {
          key,
          name: intl.formatMessage({
            id: 'EfS4CiAaMOrYoPW6ICRM',
            defaultMessage: '{count, plural, one {1x Day Bed} other {#x Day Beds}}',
          }, { count }),
          Icon,
        }
      case 'MURPHY_BED':
        return {
          key,
          name: intl.formatMessage({
            id: '_8MdMN4vEwYlBOf0-hRC',
            defaultMessage: '{count, plural, one {1x Murphy Bed} other {#x Murphy Beds}}',
          }, { count }),
          Icon,
        }
      case 'TRUNDLE_BED':
        return {
          key,
          name: intl.formatMessage({
            id: 't8NY8cjZXfJtSOJrR1Vt',
            defaultMessage: '{count, plural, one {1x Trundle Bed} other {#x Trundle Beds}}',
          }, { count }),
          Icon,
        }
      case 'FUTON':
        return {
          key,
          name: intl.formatMessage({
            id: 'RR2y5rMp2_aWLScx0kRD',
            defaultMessage: '{count, plural, one {1x Futon} other {#x Futons}}',
          }, { count }),
          Icon,
        }
      case 'WATER_BED':
        return {
          key,
          name: intl.formatMessage({
            id: 'B1xurSlkuATfByhRoVeo',
            defaultMessage: '{count, plural, one {1x Water Bed} other {#x Water Beds}}',
          }, { count }),
          Icon,
        }
      case 'OTHER':
        return {
          key,
          name: intl.formatMessage({
            id: 'YJnq5E5-T7GpioBiNNyI',
            defaultMessage: '{count, plural, one {1x Other} other {#x Other}}',
          }, { count }),
          Icon,
        }
      case 'FUTONBED':
        return {
          key,
          name: intl.formatMessage({
            id: 'e5TyL4lgDeHqXSLAKc5a',
            defaultMessage: '{count, plural, one {1x Futon Bed} other {#x Futon Beds}}',
          }, { count }),
          Icon,
        }
    }
  }), [arbitraryBedConfigurations, intl])

  return <Group direction="vertical" gap={8}>
    <Heading variant="heading4" as="h4">{bedGroupsTitle}</Heading>
    <BedGroup>
      {displayBedConfigurations.map(({ key, name, Icon }) => <BedConfiguration key={key}>
        <Icon size="S" />{name}
      </BedConfiguration>)}
    </BedGroup>
  </Group>
}

export default memo(PropertyBedGroups)
